import { useState } from "react";
import { Icons, navItems } from "../components/Nav-Items";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
  useSidebar,
} from "../components/ui/sidebar";
import { cn } from "../lib/utils";
import GlobalSearchBar from "../components/GlobalSearchBar";

// Logo component that shows full or icon-only based on sidebar state
function Logo() {
  return (
    <a
      href="/Dashboard"
      className="font-normal flex space-x-2 items-center text-sm text-black py-1 relative z-20"
    >
<div className="w-8 h-8 bg-[#006de2] rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        B+
      </div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="font-semibold text-gray-800 whitespace-pre"
      >
        BuilderPro+
      </motion.span>
    </a>
  );
}

function LogoIcon() {
  return (
    <a
      href="/Dashboard"
      className="font-normal flex space-x-2 items-center text-sm text-black py-1 relative z-20"
    >
<div className="w-8 h-8 bg-[#006de2] rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        B+
      </div>
    </a>
  );
}

// Sidebar content component
function SidebarContent({ location, setLocation }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { open } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState({});

  const handleNavigate = (e, name, path) => {
    e.preventDefault();
    setLocation(name);
    navigate(path);
  };

  const handleSignOut = async (e) => {
    e.preventDefault();
    await logout();
    navigate("/");
  };

  const toggleMenu = (name) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  return (
    <>
      <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {open ? <Logo /> : <LogoIcon />}
        <div className="mt-8 flex flex-col gap-1">
          {navItems.map((item, idx) => {
            const isActive = location === item.name;
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const isExpanded = expandedMenus[item.name];

            if (hasChildren) {
              // Render expandable menu item
              return (
                <div key={idx}>
                  <button
                    onClick={() => open && toggleMenu(item.name)}
                    className={cn(
                      "flex items-center justify-start gap-2 group/sidebar py-2 w-full rounded-lg px-2 transition-colors",
                      isActive
                        ? "font-medium"
                        : ""
                    )}
                    style={isActive ? { backgroundColor: 'color-mix(in oklab, #006de2 10%, transparent)' } : undefined}
                  >
                    <item.icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isActive ? "text-[#006de2]" : "text-[#727272]"
                      )}
                    />
                    <motion.span
                      animate={{
                        display: open ? "inline-block" : "none",
                        opacity: open ? 1 : 0,
                      }}
                      className={cn(
                        "text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0 flex-1 text-left",
                        isActive ? "text-[#006de2]" : "text-[#424242]"
                      )}
                    >
                      {item.name}
                    </motion.span>
                    {open && (
                      <motion.svg
                        animate={{ rotate: isExpanded ? 90 : 0 }}
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </motion.svg>
                    )}
                  </button>
                  {/* Sub-items */}
                  {open && (
                    <motion.div
                      initial={false}
                      animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0,
                      }}
                      className="overflow-hidden pl-7"
                    >
                      {item.children.map((sub, subIdx) => {
                        const isSubActive = location === sub.name;
                        return (
                          <button
                            key={subIdx}
                            onClick={(e) => handleNavigate(e, sub.name, sub.path)}
                            className={cn(
                              "block w-full text-sm text-left px-3 py-1.5 rounded-md transition-colors mt-1",
                              isSubActive
                                ? "font-medium text-[#006de2]"
                                : "text-[#727272]"
                            )}
                            style={isSubActive ? { backgroundColor: 'color-mix(in oklab, #006de2 10%, transparent)' } : undefined}
                          >
                            {sub.name}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </div>
              );
            }

            // Render regular link
            return (
              <SidebarLink
                key={idx}
                link={{
                  label: item.name,
                  href: item.path,
                  icon: (
                    <item.icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isActive ? "text-[#006de2]" : "text-[#727272]"
                      )}
                    />
                  ),
                }}
                onClick={(e) => handleNavigate(e, item.name, item.path)}
                className={cn(
                  "rounded-lg px-2 transition-colors",
                  isActive
                    ? "font-medium"
                    : ""
                )}
                labelClassName={isActive ? "text-[#006de2]" : undefined}
                style={isActive ? { backgroundColor: 'color-mix(in oklab, #006de2 10%, transparent)' } : undefined}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-auto pt-4">
        <SidebarLink
          link={{
            label: "Logout",
            href: "#",
            icon: (
              <Icons.Logout className="h-5 w-5 flex-shrink-0 text-red-600" />
            ),
          }}
          onClick={handleSignOut}
          className="text-red-600 hover:bg-red-50 rounded-lg px-2"
          labelClassName="text-red-600"
        />
      </div>
    </>
  );
}

// Main Layout Component
function MainLayout({ location, setLocation, children }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'rgba(0, 109, 226, 0.02)' }}>
      <Sidebar open={open} setOpen={setOpen}>
        {/* Sidebar background color is here */}
        <SidebarBody className="justify-between gap-10" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
          <SidebarContent location={location} setLocation={setLocation} />
        </SidebarBody>
      </Sidebar>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 pt-2 relative">
        {/* Background div behind content panel */}
        <div className="absolute top-2 left-0 right-0 bottom-0 rounded-tl-2xl z-0" style={{ backgroundColor: 'rgb(255, 255, 255)' }} />
        {/* Controls background of everything but the sidebar and the area on top of it - content area background color */}
        <div className="flex-1 flex flex-col rounded-tl-2xl border-t border-l border-gray-200 overflow-hidden relative z-10" style={{ backgroundColor: 'rgba(0, 109, 226, 0.01)' }}>
          {/* Desktop Header */}
          <header className="hidden md:flex items-center justify-between px-5 py-3.5 border-b border-gray-100" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
            <h1 className="text-lg font-medium text-gray-900 flex-shrink-0">{location}</h1>

            {/* Global Search Bar */}
            <GlobalSearchBar />

            <div
              onClick={() => navigate("/profile")}
              className="w-8 h-8 rounded-full overflow-hidden cursor-pointer flex-shrink-0"
              title="Go to Profile"
            >
              <img
                src="/Scooter.jpeg"
                alt="User Avatar"
                className="w-full h-full object-cover"
              />
            </div>
          </header>

          {/* Mobile Header */}
          <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-100" style={{ backgroundColor: 'rgba(0, 109, 226, 0.02)' }}>
            <h1 className="text-lg font-medium text-gray-900 flex-shrink-0">{location}</h1>

            {/* Global Search Bar (Mobile) */}
            <GlobalSearchBar />

            <div
              onClick={() => navigate("/profile")}
              className="w-8 h-8 rounded-full overflow-hidden cursor-pointer flex-shrink-0"
              title="Go to Profile"
            >
              <img
                src="/Scooter.jpeg"
                alt="User Avatar"
                className="w-full h-full object-cover"
              />
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">{children}</main>
        </div>
      </div>
    </div>
  );
}

export default MainLayout;
